import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useQuery } from '@tanstack/react-query';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { ExpandableText } from 'src/components/ExpandableText';
import { ListItem } from 'src/components/ListItem';
import { Typography } from 'src/components/Typography';
import { Appointment } from 'src/models/appointment/_types';
import { fetchResources as fetchAppointmentResources } from 'src/models/appointment/appointment';
import { getAppointmentStaff } from 'src/models/appointment/utils';
import { getUserDisplayName } from 'src/models/user/utils';
import { Visit } from 'src/models/visit/_types';
import { fetchResources as fetchVisitResources } from 'src/models/visit/visit';
import { ImageOrLogo } from 'src/shared/ImageOrLogo';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import {
  formatMonthDayYear,
  formatStartAndEndTime,
} from 'src/utils/dateFormatter';

import { AppointmentCardSection } from '../AppointmentCardSection';

import { VisitListItem } from './AppointmentMembersSection';
import { ZoomListItem } from './ZoomListItem';

interface AppointmentProps {
  appointment: Appointment;
  visit?: Visit;
}

export const ClientAppointmentDetails = ({
  appointment,
  visit,
}: AppointmentProps) => {
  const staffMember = getAppointmentStaff(appointment)[0];

  const { data: appointmentResources, isLoading: appointmentResourcesLoading } =
    useQuery({
      queryKey: ['appointment_resources', appointment.id],

      queryFn: () => {
        return fetchAppointmentResources(appointment);
      },
    });

  const { data: visitResources, isLoading: visitResourcesLoading } = useQuery({
    queryKey: ['visit_resources', visit?.id],

    queryFn: () => {
      if (!visit) return [];

      return fetchVisitResources(visit);
    },
  });

  return (
    <>
      <Box sx={{ p: 2, pt: 0 }}>
        <ImageOrLogo imageUrl={appointment.service.thumbnail} width="100%" />
        <Typography mt={3} variant="h5">
          {appointment.serviceName}
        </Typography>
      </Box>
      <Box pb={2}>
        <ListItem
          icon={icon({ name: 'clock', style: 'regular' })}
          primary={formatMonthDayYear(appointment.startTime)}
          secondary={formatStartAndEndTime(
            appointment.startTime,
            appointment.endTime
          )}
          indent="large"
          centerIndent
        />
        {appointment.location && (
          <ListItem
            icon={icon({ name: 'map-marker-alt', style: 'regular' })}
            primary={appointment.location.name}
            secondary={appointment.location.address}
            indent="large"
            centerIndent
          />
        )}
        {!appointmentResourcesLoading &&
          appointmentResources.length > 0 &&
          appointmentResources.map((resource) => (
            <ListItem
              key={resource.id}
              icon={icon({ name: 'file', style: 'regular' })}
              primary={resource.name}
              secondary={resource.visitDescription}
              indent="large"
              centerIndent
            />
          ))}
        {!visitResourcesLoading &&
          visitResources.length > 0 &&
          visitResources.map((resource) => (
            <ListItem
              key={resource.id}
              icon={icon({ name: 'file', style: 'regular' })}
              primary={resource.name}
              secondary={resource.visitDescription}
              indent="large"
              centerIndent
            />
          ))}
        {staffMember ? (
          <>
            <ListItem
              key={staffMember?.user?.id}
              avatar={<Avatar src={staffMember?.user?.profileImage} />}
              primary={getUserDisplayName(staffMember?.user)}
              secondary="Instructor"
              indent="large"
              centerIndent
            />
            <ZoomListItem appointment={appointment} />
          </>
        ) : null}
        <Box sx={{ p: 2 }}>
          <ExpandableText showMore={true} lines={3}>
            <Typography variant="body2" color="text.secondary">
              {appointment.description}
            </Typography>
          </ExpandableText>
        </Box>
      </Box>

      <PermissionCheck can="viewAppointmentMembers" object={appointment}>
        <AppointmentCardSection
          disableChildPadding
          title={`${appointment.visits.length} Going`}
        >
          {appointment.visits.map((visit) => (
            <VisitListItem
              key={visit.id}
              visit={visit}
              appointment={appointment}
              showStatus={false}
            />
          ))}
        </AppointmentCardSection>
      </PermissionCheck>
    </>
  );
};
